/**
 * CORE-730 — single source of truth for "should the Google/Apple sign-in row
 * be shown to this visitor?".
 *
 * The gate used to be copied inline into SocialAuthButtons.tsx (web) and
 * SocialAuthButtons.native.tsx, so the two surfaces could — and did — drift:
 * a visitor in the restricted (RU) region saw the buttons on the mobile path
 * but not on desktop (AMB-269 hides social sign-in in Russia). Both variants
 * now feed their region state through this one predicate.
 *
 * The region *mechanism* is unchanged (see `services/region.ts` +
 * `utils/restrictedRegion.ts`): GeoIP via `/api/region` with a browser-timezone
 * fallback. This module only decides what to do with the answer.
 */
export type SocialAuthGateInput = {
  /** The visitor resolved to a restricted (RU) audience by any region signal. */
  restricted: boolean;
  /** The authoritative `/api/region` lookup has settled at least once. */
  regionResolved: boolean;
  /** At least one provider is enabled and available on this surface. */
  hasProvider: boolean;
};

/**
 * Returns true only when the social-auth row may be shown:
 *   - a provider is actually enabled,
 *   - the region lookup has settled (so the row can't flash in and then
 *     vanish, and can't stick around in RU while GeoIP is momentarily
 *     unavailable — the "торчат в РФ" report), and
 *   - the settled region is not restricted.
 *
 * Defaulting to hidden until the region is known is the safe choice here:
 * revealing Google/Apple to a Russian visitor sends them into an OAuth flow
 * that fails from RU, whereas a brief hidden state costs nothing (the row
 * already waits on `/api/auth/providers` and Google's GIS script anyway).
 */
export function shouldShowSocialAuth({ restricted, regionResolved, hasProvider }: SocialAuthGateInput): boolean {
  if (!hasProvider) return false;
  if (!regionResolved) return false;
  return !restricted;
}
